import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Key, FolderOpen, ArrowRight, ArrowLeft, Check, Eye, EyeOff,
  ShieldCheck, HardDrive, Lock, Wifi, Rocket, FileText, BookOpen,
  Workflow, CheckCircle2, Save, History, Map, Download, PlayCircle,
} from 'lucide-react';

// --- Constants ---------------------------------------------------------------

export const FIRST_RUN_KEY = 'wf_first_run_complete';

export function isFirstRunComplete(): boolean {
  return localStorage.getItem(FIRST_RUN_KEY) === 'true';
}

export function markFirstRunComplete(data: FirstRunData): void {
  localStorage.setItem(FIRST_RUN_KEY, 'true');
  localStorage.setItem('wf_setup_data', JSON.stringify(data));
  // Write firstrun.json via Electron if available
  try {
    const api = (window as any).electronAPI;
    if (api?.writeFirstRunFlag) {
      api.writeFirstRunFlag(data);
    }
  } catch (_) {}
}

// --- Types -------------------------------------------------------------------

interface FirstRunData {
  licenseKey: string;
  workspacePath: string;
  createExample: boolean;
  importSample: boolean;
  openTutorial: boolean;
  autoBackups: boolean;
  versionHistory: boolean;
}

// --- Left brand panel — dynamic per step ------------------------------------

const STEP_PANELS = [
  {
    title: 'Welcome',
    subtitle:
      'Workflow Manager is the enterprise platform for process excellence, value stream mapping, and lean consulting.',
    icon: <Rocket size={42} className="text-blue-400/50" />,
    features: [
      {
        icon: <Workflow size={14} />,
        label: 'Value Stream Mapping',
        desc: 'Professional VSM canvas with drag-and-drop',
      },
      {
        icon: <ShieldCheck size={14} />,
        label: 'Enterprise Security',
        desc: 'Multi-tenant isolation and role-based access',
      },
    ],
  },
  {
    title: 'Security & Authentication',
    subtitle:
      'Enter your corporate license key. Our system validates locally for maximum security.',
    icon: <ShieldCheck size={42} className="text-blue-400/50" />,
    features: [
      {
        icon: <ShieldCheck size={14} />,
        label: 'Corporate License',
        desc: 'Key validated locally without internet',
      },
      {
        icon: <Lock size={14} />,
        label: 'Data Protection',
        desc: 'Encryption at rest and in transit',
      },
    ],
  },
  {
    title: 'Offline-First Architecture',
    subtitle:
      'Workflow Manager is designed for factory-floor deployment. Your maps and projects are stored locally.',
    icon: <HardDrive size={42} className="text-blue-400/50" />,
    features: [
      {
        icon: <HardDrive size={14} />,
        label: 'Local Storage',
        desc: 'Projects stay on your machine',
      },
      {
        icon: <Wifi size={14} className="line-through opacity-50" />,
        label: 'No Network Dependency',
        desc: 'Works without VPN or corporate network',
      },
    ],
  },
  {
    title: 'Get Started',
    subtitle:
      'Set up example workflows and documentation to start using Workflow Manager immediately.',
    icon: <BookOpen size={42} className="text-blue-400/50" />,
    features: [
      {
        icon: <FileText size={14} />,
        label: 'Example Templates',
        desc: 'Pre-built VSM and workflow samples',
      },
      {
        icon: <BookOpen size={14} />,
        label: 'Quick Start Guide',
        desc: 'Interactive documentation to get productive',
      },
    ],
  },
];

function BrandPanel({ step, animating }: { step: number; animating: boolean }) {
  const panel = STEP_PANELS[step] ?? STEP_PANELS[0];

  return (
    <div className="relative w-[300px] flex-shrink-0 flex flex-col justify-between p-8 overflow-hidden bg-[#080f18] border-r border-[#1a2840]">
      {/* Ambient glow */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none bg-blue-500/8 transition-colors duration-700" />
      <div className="absolute -top-32 -left-32 w-72 h-72 rounded-full bg-blue-900/20 blur-3xl pointer-events-none" />

      <div className="relative z-10 flex flex-col h-full">
        {/* Logo — always fixed */}
        <div className="flex items-center gap-3 mb-8 flex-shrink-0">
          <img
            src="./assets/logo/workflow-icon.png"
            alt="Workflow Manager"
            className="h-9 w-9 object-contain flex-shrink-0"
          />
          <div>
            <div className="text-sm font-bold text-slate-100 leading-none tracking-wide">WORKFLOW MANAGER</div>
            <div className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-widest">
              Enterprise Excellence Platform
            </div>
          </div>
        </div>

        {/* Dynamic content */}
        <div
          className={`flex-1 transition-all duration-300 ${
            animating ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'
          }`}
        >
          {/* Icon composition */}
          <div className="mb-5 flex items-center justify-center w-16 h-16 rounded bg-blue-500/8 border border-blue-500/20">
            {panel.icon}
          </div>

          <h2 className="text-[15px] font-bold text-slate-100 leading-snug mb-2">
            {panel.title}
          </h2>
          <p className="text-xs text-slate-400 leading-relaxed mb-6">{panel.subtitle}</p>

          {/* Feature list */}
          <ul className="space-y-3.5">
            {panel.features.map(f => (
              <li key={f.label} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded bg-[#0e1a27] border border-[#1e293b] flex items-center justify-center flex-shrink-0 text-blue-400 mt-0.5">
                  {f.icon}
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-300">{f.label}</div>
                  <div className="text-[11px] text-slate-500 leading-relaxed">{f.desc}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Version footer */}
      <div className="relative z-10 flex items-center gap-1.5 pt-4 flex-shrink-0">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
        <span className="text-[11px] text-slate-500">v1.0.0 · Build 2026.03.09</span>
      </div>
    </div>
  );
}

// --- Step 0 — Welcome -------------------------------------------------------

function Step0Welcome() {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center space-y-6 py-8">
      <div className="relative">
        <div className="absolute inset-0 w-20 h-20 mx-auto rounded-full bg-blue-500/20 blur-xl" />
        <img
          src="./assets/logo/workflow-icon.png"
          alt=""
          className="relative w-16 h-16 object-contain mx-auto"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-slate-100 tracking-wide mb-1">WORKFLOW MANAGER</h2>
        <p className="text-sm text-blue-400/80 font-medium uppercase tracking-widest">Enterprise Excellence Platform</p>
      </div>
      <p className="text-sm text-slate-400 leading-relaxed max-w-sm">
        Welcome to the professional platform for value stream mapping, workflow execution, 
        and lean consulting. This wizard will configure your environment in a few steps.
      </p>
      <div className="grid grid-cols-3 gap-4 w-full max-w-sm">
        {[
          { icon: <Workflow size={16} />, label: 'Process Mapping' },
          { icon: <ShieldCheck size={16} />, label: 'Enterprise Security' },
          { icon: <HardDrive size={16} />, label: 'Offline-First' },
        ].map(f => (
          <div key={f.label} className="flex flex-col items-center gap-2 p-3 rounded bg-[#0e1a27] border border-[#1e293b]">
            <div className="text-blue-400">{f.icon}</div>
            <span className="text-[10px] text-slate-400 font-medium">{f.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Step 1 — License Key + Accept -----------------------------------------

function Step1License({
  value,
  onChange,
  eulaAccepted,
  onEulaChange,
}: {
  value: string;
  onChange: (v: string) => void;
  eulaAccepted: boolean;
  onEulaChange: (v: boolean) => void;
}) {
  const [show, setShow] = useState(false);
  const isValid = value.trim().length >= 8;

  return (
    <div className="space-y-5">
      <div>
        <div className="inline-flex items-center justify-center w-11 h-11 rounded bg-blue-500/10 border border-blue-500/20 mb-3">
          <Key size={20} className="text-blue-400" />
        </div>
        <h2 className="text-xl font-bold text-slate-100 mb-1">License Key</h2>
        <p className="text-sm text-slate-400 leading-relaxed">
          Enter the license key provided with your purchase to activate Workflow Manager.
        </p>
      </div>

      {/* Key input */}
      <div className="space-y-2">
        <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider">
          License Key
        </label>
        <div className="relative">
          <input
            type={show ? 'text' : 'password'}
            value={value}
            onChange={e => onChange(e.target.value.toUpperCase())}
            placeholder="XXXX-XXXX-XXXX-XXXX"
            spellCheck={false}
            autoComplete="off"
            className="w-full bg-[#060d16] border border-[#1e293b] rounded px-4 py-3 pr-11 text-sm font-mono text-slate-200 placeholder:text-slate-700 focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/10 transition-all tracking-widest"
          />
          <button
            type="button"
            onClick={() => setShow(v => !v)}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
          >
            {show ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>
        {value.length > 0 && (
          <div
            className={`flex items-center gap-2 text-xs font-medium ${
              isValid ? 'text-emerald-400' : 'text-amber-400'
            }`}
          >
            <span
              className={`inline-block w-1.5 h-1.5 rounded-full ${
                isValid ? 'bg-emerald-400' : 'bg-amber-400'
              }`}
            />
            {isValid ? 'Key format recognized' : 'Key too short — minimum 8 characters'}
          </div>
        )}
      </div>

      {/* License validation info */}
      <div className="p-4 rounded bg-blue-500/5 border border-blue-500/15 space-y-2.5">
        {[
          'License validated locally — no internet required',
          'Bound to this machine for security',
          'Full EULA accepted during installation',
        ].map(text => (
          <div key={text} className="flex items-center gap-2">
            <CheckCircle2 size={13} className="text-blue-400 flex-shrink-0" />
            <span className="text-xs text-blue-300/80 font-medium">{text}</span>
          </div>
        ))}
      </div>

      {/* Accept checkbox */}
      <label className="flex items-start gap-3 cursor-pointer group mt-2">
        <div className="relative mt-0.5 flex-shrink-0">
          <input
            type="checkbox"
            checked={eulaAccepted}
            onChange={e => onEulaChange(e.target.checked)}
            className="sr-only"
          />
          <div
            className={`w-4 h-4 rounded flex items-center justify-center border transition-all duration-150 ${
              eulaAccepted
                ? 'bg-blue-600 border-blue-600'
                : 'bg-[#060d16] border-[#1e293b] group-hover:border-blue-500/50'
            }`}
          >
            {eulaAccepted && <Check size={10} className="text-white" strokeWidth={3} />}
          </div>
        </div>
        <span
          className={`text-xs leading-relaxed transition-colors select-none ${
            eulaAccepted ? 'text-slate-300' : 'text-slate-500 group-hover:text-slate-400'
          }`}
        >
          I accept the license terms and conditions
        </span>
      </label>
    </div>
  );
}

// --- Step 2 — Workspace Configuration ---------------------------------------

const DEFAULT_WS = 'C:\\Users\\Documentos\\Workflow Manager';

function Step2Workspace({
  value,
  onChange,
  autoBackups,
  onAutoBackupsChange,
  versionHistory,
  onVersionHistoryChange,
}: {
  value: string;
  onChange: (v: string) => void;
  autoBackups: boolean;
  onAutoBackupsChange: (v: boolean) => void;
  versionHistory: boolean;
  onVersionHistoryChange: (v: boolean) => void;
}) {
  const [browsing, setBrowsing] = useState(false);

  const handleBrowse = async () => {
    const api = (window as any).electronAPI;
    if (api?.selectFolder) {
      setBrowsing(true);
      try {
        const selected: string | null = await api.selectFolder();
        if (selected) onChange(selected);
      } finally {
        setBrowsing(false);
      }
    } else {
      onChange(DEFAULT_WS);
    }
  };

  const displayPath = value || DEFAULT_WS;

  return (
    <div className="space-y-5">
      <div>
        <div className="inline-flex items-center justify-center w-11 h-11 rounded bg-blue-500/10 border border-blue-500/20 mb-3">
          <FolderOpen size={20} className="text-blue-400" />
        </div>
        <h2 className="text-xl font-bold text-slate-100 mb-1">Workspace</h2>
        <p className="text-sm text-slate-400 leading-relaxed">
          Choose where your projects and maps will be stored locally.
        </p>
      </div>

      <div className="space-y-2">
        <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider">
          Project Directory
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={displayPath}
            onChange={e => onChange(e.target.value)}
            className="flex-1 min-w-0 bg-[#060d16] border border-[#1e293b] rounded px-4 py-3 text-xs font-mono text-slate-300 focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/10 transition-all truncate"
          />
          <button
            type="button"
            onClick={handleBrowse}
            disabled={browsing}
            className="flex items-center gap-2 px-4 py-3 bg-[#0e1a27] hover:bg-[#152236] border border-[#1e293b] hover:border-blue-500/30 rounded text-xs font-semibold text-slate-300 hover:text-blue-300 transition-all whitespace-nowrap disabled:opacity-50"
          >
            <FolderOpen size={14} />
            {browsing ? 'Wait...' : 'Browse'}
          </button>
        </div>
      </div>

      {/* Workspace options */}
      <div className="space-y-3">
        <WizardCheckbox
          checked={autoBackups}
          onChange={onAutoBackupsChange}
          icon={<Save size={14} />}
          label="Enable automatic backups"
          desc="Periodically save backup copies of your projects"
        />
        <WizardCheckbox
          checked={versionHistory}
          onChange={onVersionHistoryChange}
          icon={<History size={14} />}
          label="Enable local version history"
          desc="Keep track of changes with restorable snapshots"
        />
      </div>

      <div className="p-3.5 rounded bg-[#060d16] border border-[#1e293b]">
        <p className="text-[11px] text-slate-500 leading-relaxed">
          Projects are stored locally for maximum performance and offline reliability.
          We recommend a dedicated folder on a local SSD drive.
        </p>
      </div>
    </div>
  );
}

// --- Shared checkbox component -----------------------------------------------

function WizardCheckbox({
  checked,
  onChange,
  icon,
  label,
  desc,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  icon: React.ReactNode;
  label: string;
  desc: string;
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer group p-3 rounded bg-[#0e1a27] border border-[#1e293b] hover:border-blue-500/30 transition-all">
      <div className="relative mt-0.5 flex-shrink-0">
        <input
          type="checkbox"
          checked={checked}
          onChange={e => onChange(e.target.checked)}
          className="sr-only"
        />
        <div
          className={`w-4 h-4 rounded flex items-center justify-center border transition-all duration-150 ${
            checked
              ? 'bg-blue-600 border-blue-600'
              : 'bg-[#060d16] border-[#1e293b] group-hover:border-blue-500/50'
          }`}
        >
          {checked && <Check size={10} className="text-white" strokeWidth={3} />}
        </div>
      </div>
      <div className="flex items-start gap-2.5 flex-1 min-w-0">
        <div className="text-blue-400 mt-0.5 flex-shrink-0">{icon}</div>
        <div>
          <div className="text-sm font-semibold text-slate-200">{label}</div>
          <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">{desc}</div>
        </div>
      </div>
    </label>
  );
}

// --- Step 3 — Get Started ----------------------------------------------------

function Step3GetStarted({
  createExample,
  onCreateExampleChange,
  importSample,
  onImportSampleChange,
  openTutorial,
  onOpenTutorialChange,
}: {
  createExample: boolean;
  onCreateExampleChange: (v: boolean) => void;
  importSample: boolean;
  onImportSampleChange: (v: boolean) => void;
  openTutorial: boolean;
  onOpenTutorialChange: (v: boolean) => void;
}) {
  return (
    <div className="space-y-5">
      <div>
        <div className="inline-flex items-center justify-center w-11 h-11 rounded bg-blue-500/10 border border-blue-500/20 mb-3">
          <Rocket size={20} className="text-blue-400" />
        </div>
        <h2 className="text-xl font-bold text-slate-100 mb-1">Get Started</h2>
        <p className="text-sm text-slate-400 leading-relaxed">
          Choose how you'd like to begin using Workflow Manager.
        </p>
      </div>

      <div className="space-y-3">
        <WizardCheckbox
          checked={createExample}
          onChange={onCreateExampleChange}
          icon={<Map size={14} />}
          label="Create example VSM project"
          desc="Generate a sample value stream map to explore the platform"
        />
        <WizardCheckbox
          checked={importSample}
          onChange={onImportSampleChange}
          icon={<Download size={14} />}
          label="Import sample workflow"
          desc="Load a pre-built workflow template with tasks and stages"
        />
        <WizardCheckbox
          checked={openTutorial}
          onChange={onOpenTutorialChange}
          icon={<PlayCircle size={14} />}
          label="Open quick tutorial"
          desc="Watch a 2-minute interactive guide to get productive fast"
        />
      </div>

      <div className="p-3.5 rounded bg-[#060d16] border border-[#1e293b]">
        <p className="text-[11px] text-slate-500 leading-relaxed">
          These options can be changed later in Settings.
        </p>
      </div>
    </div>
  );
}

// --- Main component ----------------------------------------------------------

export const FirstRunSetup: React.FC = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');

  const [licenseKey, setLicenseKey] = useState('');
  const [eulaAccepted, setEulaAccepted] = useState(false);
  const [workspacePath, setWorkspacePath] = useState('');
  const [autoBackups, setAutoBackups] = useState(true);
  const [versionHistory, setVersionHistory] = useState(true);
  const [createExample, setCreateExample] = useState(true);
  const [importSample, setImportSample] = useState(false);
  const [openTutorial, setOpenTutorial] = useState(false);

  const TOTAL_STEPS = 4;

  const canAdvance = [
    true,                                         // Welcome — always
    licenseKey.trim().length >= 8 && eulaAccepted, // License
    true,                                         // Workspace
    true,                                         // Get Started
  ][step] ?? false;

  function transition(nextStep: number, dir: 'forward' | 'back') {
    setDirection(dir);
    setAnimating(true);
    setTimeout(() => {
      setStep(nextStep);
      setAnimating(false);
    }, 200);
  }

  function handleNext() {
    if (step < TOTAL_STEPS - 1) {
      transition(step + 1, 'forward');
    } else {
      const finalWorkspace = workspacePath || DEFAULT_WS;
      markFirstRunComplete({
        licenseKey,
        workspacePath: finalWorkspace,
        createExample,
        importSample,
        openTutorial,
        autoBackups,
        versionHistory,
      });

      // Sync license key to LicenseProvider storage so it doesn't ask again
      try {
        localStorage.setItem('wf_license_key', licenseKey.trim().toUpperCase());
      } catch (_) {}

      // Execute onboarding actions via Electron IPC (failsafe — skip silently if unavailable)
      const api = (window as any).electronAPI;
      const actions: Promise<void>[] = [];

      if (createExample && api?.setupCreateExample) {
        actions.push(
          api.setupCreateExample(finalWorkspace).catch(() => {})
        );
      }

      if (openTutorial && api?.setupOpenTutorial) {
        actions.push(
          api.setupOpenTutorial().catch(() => {})
        );
      }

      // Wait for actions to finish then navigate to dashboard
      Promise.all(actions).finally(() => {
        navigate('/centro-operacional', { replace: true });
      });
    }
  }

  function handleBack() {
    if (step > 0) transition(step - 1, 'back');
  }

  const stepLabels = ['Welcome', 'License', 'Workspace', 'Get Started'];
  const isLastStep = step === TOTAL_STEPS - 1;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#050c14] overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] rounded-full bg-blue-950/40 blur-[140px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-blue-950/25 blur-[120px]" />
      </div>

      {/* Main card — split layout, fixed height for anti-jump */}
      <div className="relative flex w-full max-w-[820px] mx-4 h-[580px] rounded overflow-hidden shadow-[0_32px_100px_rgba(0,0,0,0.75)] border border-[#1a2840]">

        {/* Left brand panel */}
        <BrandPanel step={step} animating={animating} />

        {/* Right wizard panel */}
        <div className="flex-1 flex flex-col bg-[#0a1522]">

          {/* Step progress bar */}
          <div className="px-8 pt-6 pb-5 border-b border-[#1a2840] flex-shrink-0">
            <div className="flex items-center gap-4">
              {stepLabels.map((label, i) => (
                <React.Fragment key={label}>
                  {i > 0 && (
                    <div
                      className={`h-px flex-1 transition-all duration-500 ${
                        i <= step ? 'bg-blue-500/60' : 'bg-[#1e293b]'
                      }`}
                    />
                  )}
                  <div
                    className={`flex items-center gap-2 transition-all duration-300 ${
                      i === step
                        ? 'text-blue-400'
                        : i < step
                        ? 'text-slate-400'
                        : 'text-slate-600'
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-bold flex-shrink-0 transition-all duration-300 ${
                        i < step
                          ? 'bg-blue-600/70 text-white'
                          : i === step
                          ? 'bg-blue-600 text-white shadow-[0_0_12px_rgba(59,130,246,0.4)]'
                          : 'bg-[#0e1a27] border border-[#1e293b] text-slate-600'
                      }`}
                    >
                      {i < step ? <Check size={12} /> : i + 1}
                    </div>
                    <span className="text-[12px] font-bold">{label}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Step content — scrollable overflow for safety */}
          <div className="flex-1 overflow-y-auto px-8 py-6 min-h-0">
            <div
              className={`transition-all duration-200 ${
                animating
                  ? direction === 'forward'
                    ? 'opacity-0 translate-x-4'
                    : 'opacity-0 -translate-x-4'
                  : 'opacity-100 translate-x-0'
              }`}
            >
              {step === 0 && <Step0Welcome />}
              {step === 1 && (
                <Step1License
                  value={licenseKey}
                  onChange={setLicenseKey}
                  eulaAccepted={eulaAccepted}
                  onEulaChange={setEulaAccepted}
                />
              )}
              {step === 2 && (
                <Step2Workspace
                  value={workspacePath}
                  onChange={setWorkspacePath}
                  autoBackups={autoBackups}
                  onAutoBackupsChange={setAutoBackups}
                  versionHistory={versionHistory}
                  onVersionHistoryChange={setVersionHistory}
                />
              )}
              {step === 3 && (
                <Step3GetStarted
                  createExample={createExample}
                  onCreateExampleChange={setCreateExample}
                  importSample={importSample}
                  onImportSampleChange={setImportSample}
                  openTutorial={openTutorial}
                  onOpenTutorialChange={setOpenTutorial}
                />
              )}
            </div>
          </div>

          {/* Footer navigation */}
          <div className="px-8 py-4 border-t border-[#1a2840] flex items-center justify-between bg-[#080f18]/60 flex-shrink-0">
            <button
              type="button"
              onClick={handleBack}
              className={`flex items-center gap-2 text-sm font-medium transition-all ${
                step === 0 ? 'invisible' : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              <ArrowLeft size={15} /> Back
            </button>

            {/* Dot step indicators */}
            <div className="flex items-center gap-2">
              {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                <div
                  key={i}
                  className={`rounded-full transition-all duration-300 ${
                    i < step
                      ? 'w-4 h-1 bg-blue-500/70'
                      : i === step
                      ? 'w-4 h-1 bg-blue-500'
                      : 'w-1.5 h-1.5 bg-[#1e293b]'
                  }`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={handleNext}
              disabled={!canAdvance}
              className="flex items-center gap-2 px-5 py-2.5 rounded text-sm font-bold transition-all duration-200 text-white shadow-lg disabled:opacity-40 disabled:cursor-not-allowed bg-blue-600 hover:bg-blue-500 active:bg-blue-700"
            >
              {isLastStep ? 'Launch Workflow Manager' : step === 0 ? 'Get Started' : 'Next'}
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirstRunSetup;
